import { actionLabel, fieldLabel, hasDeviceInfo, meaningful } from '@speles7172/audit-client/core';
import type { AuditRecord } from '@peles/shared';
import { Monitor, MapPin } from 'lucide-react';

/**
 * One record's history, as a timeline.
 *
 * Entity-agnostic on purpose: it takes records and renders them, so the same
 * component serves an event, a vendor, an invoice or anything audited later.
 *
 * The labelling comes from `@speles7172/audit-client/core` rather than a local
 * map — `actionLabel` turns `status_change` into "Status Changed" and
 * `fieldLabel` turns `vendor_id` into "Vendor", and the backend writes those
 * exact strings. A second copy here would drift the first time a verb is added.
 */
export function AuditTrail({ records }: { records: AuditRecord[] }) {
  if (records.length === 0) {
    return <p className="text-sm text-gray-500">No changes recorded yet.</p>;
  }

  return (
    <ol className="space-y-4 border-l border-gray-200 pl-4">
      {records.map((record) => (
        <AuditTrailEntry key={record.id} record={record} />
      ))}
    </ol>
  );
}

const ACTION_STYLES: Record<string, string> = {
  create: 'bg-green-100 text-green-700',
  bulk_create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  status_change: 'bg-amber-100 text-amber-700',
  delete: 'bg-red-100 text-red-700',
  login: 'bg-gray-100 text-gray-600',
  view: 'bg-indigo-100 text-indigo-700',
};

function AuditTrailEntry({ record }: { record: AuditRecord }) {
  const badge = ACTION_STYLES[record.action] ?? 'bg-gray-100 text-gray-700';

  return (
    <li className="relative">
      <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-gray-300 ring-2 ring-white" />

      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badge}`}>
          {actionLabel(record.action)}
        </span>
        <span className="text-xs text-gray-500">
          by <span className="font-medium text-gray-700">{actorName(record)}</span>
        </span>
        <time className="ml-auto text-xs text-gray-400" dateTime={record.occurredAt}>
          {new Date(record.occurredAt).toLocaleString()}
        </time>
      </div>

      {record.message && <p className="mt-1 text-sm text-gray-600">{record.message}</p>}

      {record.changes && record.changes.length > 0 && (
        <dl className="mt-2 space-y-1 rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-xs">
          {record.changes.map((change, index) => (
            <div key={`${change.field}-${index}`} className="flex flex-wrap items-center gap-2">
              <dt className="font-medium text-gray-600">{fieldLabel(change.field)}</dt>
              <dd className="flex flex-wrap items-center gap-2">
                {/* An absent side is an em dash, not nothing: "Due Date → 1 Sep"
                    only reads as "was previously unset" if the blank is visible. */}
                <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700 line-through">
                  {change.from ?? '—'}
                </span>
                <span className="text-gray-400">→</span>
                <span className="rounded bg-green-50 px-1.5 py-0.5 text-green-700">
                  {change.to ?? '—'}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      )}

      <AuditDevice record={record} />
    </li>
  );
}

/**
 * Where the person was and what they were using.
 *
 * Renders nothing when the record carries nothing meaningful — which is normal
 * for anything a scheduled job wrote. `hasDeviceInfo` treats the literal
 * "Unknown" as nothing, so a trail does not fill up with it.
 */
function AuditDevice({ record }: { record: AuditRecord }) {
  if (!hasDeviceInfo(record.client)) return null;
  const client = record.client!;

  const parts = [client.location, client.browser].filter(meaningful);

  return (
    <p
      className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-gray-400"
      title={client.userAgent ?? undefined}
    >
      {parts.length > 0 && (
        <>
          {meaningful(client.location) ? <MapPin size={12} /> : <Monitor size={12} />}
          <span>{parts.join(' · ')}</span>
        </>
      )}
      {meaningful(client.ipAddress) && <span className="font-mono">{client.ipAddress}</span>}
    </p>
  );
}

function actorName(record: AuditRecord): string {
  // "System" rather than "Unknown": a record with no actor was written by a
  // scheduled job or a webhook, not lost.
  return record.actor?.name ?? record.actor?.email ?? record.actor?.id ?? 'System';
}
